import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Activity, ArrowLeft, Ban, CalendarClock, KeyRound, PlayCircle, RotateCcw, Trash2, UserRound } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import {
  DeleteAdminDialog, ReactivateAdminDialog, ResetMfaDialog, SuspendAdminDialog,
} from '@/components/admins/AdminActionDialogs';
import { AdminStatusBadge } from '@/components/admins/AdminStatusBadge';
import { CredentialsDialog } from '@/components/admins/CredentialsDialog';
import { Page, PageHeader } from '@/components/layout/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormAlert } from '@/components/ui/form-alert';
import { Skeleton } from '@/components/ui/skeleton';
import { adminKeys, adminsApi, type MfaCredentials } from '@/lib/admins-api';
import { genericErrorMessage, httpStatus } from '@/lib/api-errors';
import { formatDateTime, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { BACKUP_CODES_LOW, BACKUP_CODES_TOTAL } from '@/routes/settings';
import { useAuthStore } from '@/stores/auth';

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="space-y-1">
    <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="text-sm">{children}</dd>
  </div>
);

const Stat = ({ icon: Icon, label, value, testId }: { icon: typeof Activity; label: string; value: ReactNode; testId: string }) => (
  <div className="flex items-center gap-3 rounded-lg border bg-white p-4">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-teal/10">
      <Icon className="h-5 w-5 text-brand-teal" aria-hidden />
    </span>
    <div className="min-w-0">
      <p className="text-xs text-muted-foreground">{label}</p>
      {/* Pas de truncate : une date-heure ne tient pas sur une tuile en 4 colonnes */}
      <p className="text-lg font-semibold tabular-nums" data-testid={testId}>
        {value}
      </p>
    </div>
  </div>
);

const BackLink = () => (
  <Link to="/admins" className={buttonVariants({ variant: 'ghost', size: 'sm', className: '-ml-3 w-fit text-muted-foreground' })}>
    <ArrowLeft className="mr-1 h-4 w-4" aria-hidden />
    Admins
  </Link>
);

type DialogName = 'suspend' | 'reactivate' | 'reset-mfa' | 'delete';

export const AdminDetailPage = () => {
  const { id } = useParams({ from: '/authed/admins/$id' });
  const selfId = useAuthStore((s) => s.admin?.id);
  const [dialog, setDialog] = useState<DialogName | null>(null);
  // Secrets du reset MFA : state local seulement, effacés à la fermeture de la modal credentials
  const [newMfa, setNewMfa] = useState<MfaCredentials | null>(null);
  const query = useQuery({ queryKey: adminKeys.detail(id), queryFn: () => adminsApi.get(id) });

  if (query.isPending) {
    return (
      <Page className="max-w-5xl">
        <BackLink />
        <div className="space-y-4" role="status" aria-label="Chargement de l'admin">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-40 w-full" />
        </div>
      </Page>
    );
  }

  if (query.isError) {
    // 400 = id non UUID : même message qu'un admin inexistant ou supprimé
    const missing = [400, 404].includes(httpStatus(query.error) ?? 0);
    return (
      <Page className="max-w-5xl">
        <BackLink />
        <FormAlert>{missing ? 'Admin introuvable' : `Impossible de charger l'admin : ${genericErrorMessage(query.error).toLowerCase()}`}</FormAlert>
        {!missing && (
          <Button variant="outline" onClick={() => void query.refetch()}>
            Réessayer
          </Button>
        )}
      </Page>
    );
  }

  const a = query.data;
  const isSelf = a.id === selfId;
  const name = [a.firstName, a.lastName].filter(Boolean).join(' ');
  const openDialog = (d: DialogName) => (open: boolean) => setDialog(open ? d : null);

  return (
    <Page className="max-w-5xl">
      <BackLink />
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            {a.email}
            <AdminStatusBadge status={a.status} />
          </span>
        }
        description={name || undefined}
        actions={
          !isSelf && (
            <>
              {a.status !== 'SUSPENDED' && (
                <Button variant="outline" onClick={() => setDialog('reset-mfa')}>
                  <RotateCcw className="mr-2 h-4 w-4" aria-hidden />
                  Reset MFA
                </Button>
              )}
              {a.status === 'SUSPENDED' ? (
                <Button className="bg-success text-white hover:bg-success/90" onClick={() => setDialog('reactivate')}>
                  <PlayCircle className="mr-2 h-4 w-4" aria-hidden />
                  Réactiver
                </Button>
              ) : (
                <Button variant="destructive" onClick={() => setDialog('suspend')}>
                  <Ban className="mr-2 h-4 w-4" aria-hidden />
                  Suspendre
                </Button>
              )}
              <Button variant="destructive" onClick={() => setDialog('delete')}>
                <Trash2 className="mr-2 h-4 w-4" aria-hidden />
                Supprimer
              </Button>
            </>
          )
        }
      />

      {isSelf && (
        <Card className="bg-muted/40" data-testid="self-info">
          <CardContent className="flex items-start gap-3 pt-6 text-sm">
            <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-brand-violet" aria-hidden />
            <p>
              C'est votre propre compte : aucune action d'administration possible ici. Votre mot de passe se change depuis les{' '}
              <Link to="/settings" className="font-medium text-brand-violet underline-offset-4 hover:underline">
                paramètres
              </Link>
              .
            </p>
          </CardContent>
        </Card>
      )}
      {!isSelf && a.status === 'SUSPENDED' && (
        <p className="text-sm text-muted-foreground" data-testid="reset-mfa-hint">
          Pour réinitialiser le MFA de cet admin, réactivez-le d'abord.
        </p>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Informations</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Email">{a.email}</Field>
            <Field label="Nom">{name || '—'}</Field>
            <Field label="Statut">
              <AdminStatusBadge status={a.status} />
            </Field>
            <Field label="MFA">
              {a.mfaEnabled ? 'Configuré' : <span className="text-amber-700">Jamais validé (première connexion attendue)</span>}
            </Field>
            <Field label="Mot de passe">
              {a.mustChangePassword ? (
                <Badge variant="warning-soft" data-testid="must-change-password">
                  Changement requis
                </Badge>
              ) : (
                'Choisi par l’admin'
              )}
            </Field>
            <Field label="Créé le">{formatDateTime(a.createdAt)}</Field>
          </dl>
        </CardContent>
      </Card>

      <section className="space-y-3" aria-labelledby="activity-title">
        <h2 id="activity-title" className="text-base font-semibold">
          Activité
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Stat icon={CalendarClock} label="Dernière connexion" value={formatDateTime(a.lastLoginAt)} testId="admin-last-login" />
          <Stat
            icon={KeyRound}
            label="Codes de secours restants"
            value={
              <span className={cn(a.mfaBackupCodesCount <= BACKUP_CODES_LOW && 'text-amber-700')}>
                {a.mfaBackupCodesCount} / {BACKUP_CODES_TOTAL}
              </span>
            }
            testId="admin-backup-codes"
          />
          <Stat icon={Activity} label="Actions sur 7 jours" value={formatNumber(a.activity.last7Days)} testId="admin-activity-7" />
          <Stat icon={Activity} label="Actions sur 30 jours" value={formatNumber(a.activity.last30Days)} testId="admin-activity-30" />
        </div>
        <p className="text-xs text-muted-foreground">Actions = événements d'audit dont l'admin est l'auteur, connexions comprises.</p>
      </section>

      {!isSelf && (
        <>
          <SuspendAdminDialog admin={a} open={dialog === 'suspend'} onOpenChange={openDialog('suspend')} />
          <ReactivateAdminDialog admin={a} mfaEnabled={a.mfaEnabled} open={dialog === 'reactivate'} onOpenChange={openDialog('reactivate')} />
          <ResetMfaDialog admin={a} open={dialog === 'reset-mfa'} onOpenChange={openDialog('reset-mfa')} onReset={setNewMfa} />
          <DeleteAdminDialog admin={a} open={dialog === 'delete'} onOpenChange={openDialog('delete')} />
        </>
      )}
      <CredentialsDialog title="MFA réinitialisé" email={a.email} credentials={newMfa} onClose={() => setNewMfa(null)} />
    </Page>
  );
};
